import { z } from "zod";

/**
 * Client identity verification (0070). Cash bookings are gated on
 * `profiles.kyc_status = 'verified'` (SECURITY §9a) and this is the only way a
 * client reaches that state.
 *
 * WHAT IS DELIBERATELY NOT HERE: an accepted-document allowlist. Which IDs
 * count is an Open Item, so `declaredDocumentType` is free text the reviewer
 * judges. When Luigi names the list it becomes an enum here and a CHECK in the
 * database — inventing one now would make a guess into a contract
 * (never-invent-policy.md).
 */

/**
 * Enforced in three places on purpose: here (client + server, one schema), the
 * `kyc_submissions.mime_type` CHECK, and the size/type read back from storage
 * rather than from the request body. These are engineering controls — upload
 * abuse, and a reviewer whose browser has to render the thing — not product
 * policy, so unlike the document list they are ours to set. HEIC is excluded:
 * the client app converts before upload rather than shipping a decoder into the
 * admin review screen.
 */
export const KYC_DOCUMENT_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export const KYC_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

/** Step 1 — ask the server for a short-lived, server-scoped upload target. */
export const kycUploadUrlSchema = z.object({
  mimeType: z.enum(KYC_DOCUMENT_MIME_TYPES),
});

export type KycUploadUrlInput = z.infer<typeof kycUploadUrlSchema>;

/**
 * The RA 10173 consent recorded with every submission (0084). Written inside
 * `record_kyc_submission()`, in the same transaction as the row and the status
 * move — `consents` grants INSERT to `client`, so the app could write it
 * itself, and that is precisely the arrangement 0084 rejects: a client calling
 * the route directly would otherwise land a document with no consent behind it.
 */
export const KYC_CONSENT_PURPOSE = "kyc_document_processing";

/**
 * ⚠ SCAFFOLD, not the binding notice. A compliant RA 10173 notice must state
 * the retention period, and **OQ-83 is unanswered** — nothing is scheduled to
 * call `purge_kyc_documents()`. So the copy is an honest placeholder in
 * `LegalScreen`'s register, and this marker deliberately does NOT reuse
 * onboarding's '2026-08-01': when counsel supplies the real text this bumps,
 * and `consents.policy_version` is what tells you who agreed to which.
 *
 * Bumped 2026-10-05 (was 'kyc-scaffold-2026-08-16'): the consent now covers a
 * selfie holding the ID (OQ-156) and states that both photos are deleted once
 * reviewed (0287). A client on the old build is refused with KYC_CONSENT_STALE
 * rather than recorded as agreeing to copy that never mentioned a selfie.
 */
export const KYC_CONSENT_POLICY_VERSION = "kyc-selfie-2026-10-05";

/**
 * Step 2 — confirm the upload. `storagePath` is echoed back from step 1 and the
 * server re-checks that it sits under the caller's own prefix and that an
 * object is actually there; the size and mime recorded are read from storage,
 * never from this body (security.md: server-authoritative).
 *
 * Two steps rather than one because a single record-then-upload call leaves a
 * row (and a 'pending' status, and a locked cash option) behind every dropped
 * mobile upload, with nothing for the reviewer to look at.
 */
export const kycSubmissionSchema = z.object({
  storagePath: z.string().min(1).max(200),
  /** OQ-156 / BL-43: a selfie holding the ID, minted by the same upload route
   *  and checked the same way. Required — 0287 refuses a submission without. */
  selfieStoragePath: z.string().min(1).max(200),
  declaredDocumentType: z.string().trim().min(1).max(64),
  /**
   * `literal(true)` rather than a boolean: a withheld consent is not a
   * submission carrying a false flag, it is not a submission. The schema
   * refuses to describe one, so no handler has to remember to check.
   *
   * `policyVersion` is sent by the client and then checked against the current
   * constant server-side. It is NOT taken on trust — a client-chosen version
   * string would be a client-authored fact inside a legal record — and it is
   * not silently stamped by the server either, because a stale tab showing
   * superseded copy should be refused, not recorded as having agreed to text it
   * never displayed.
   */
  consent: z.object({
    granted: z.literal(true),
    policyVersion: z.string().trim().min(1).max(64),
  }),
});

export type KycSubmissionInput = z.infer<typeof kycSubmissionSchema>;

/**
 * Superadmin asks for a short-lived link to one document. Carries no
 * `client_request_id`: a replay must mint a FRESH url, and an idempotent replay
 * would hand back an expired one.
 */
export const kycDocumentUrlSchema = z.object({
  submissionId: z.string().uuid(),
  /** Which of the two photos. Defaults to the ID so an older admin build keeps working. */
  image: z.enum(["document", "selfie"]).default("document"),
});

export type KycDocumentUrlInput = z.infer<typeof kycDocumentUrlSchema>;

/**
 * The review decision. Superadmin-only at the route — WHO may review is an Open
 * Item, and 0003's grant to hr/admin is a capability, not an approved authority.
 */
export const kycDecisionSchema = z
  .object({
    submissionId: z.string().uuid(),
    decision: z.enum(["verified", "rejected"]),
    /** Internal note. Never shown to the client — the column grant withholds it. */
    reason: z.string().trim().min(3).max(500).optional(),
  })
  .superRefine((input, context) => {
    // A rejection with no recorded reason leaves the audit trail unable to say
    // why a client was refused, which is the one question it will be asked.
    if (input.decision === "rejected" && !input.reason) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reason"],
        message: "A rejection must record why.",
      });
    }
  });

export type KycDecisionInput = z.infer<typeof kycDecisionSchema>;
